import React, { useCallback, useMemo, useRef } from "react";
import { DEFAULT_LOCALE_EN } from "./locale";
import { UNITS, classNames } from "./utils";
import { Select } from "antd";
import { formatValue, parsePartArray, partToString } from "./converter";
import { sort } from "./utils";

export function CustomSelect(props) {
  const {
    value,
    grid = true,
    optionsList,
    setValue,
    locale,
    className,
    humanizeLabels,
    disabled,
    readOnly,
    leadingZero,
    clockFormat,
    period,
    unit,
    periodicityOnDoubleClick,
    mode,
    allowClear,
    filterOption = () => true,
    ...otherProps
  } = props;

  const stringValue = useMemo(() => {
    if (value && Array.isArray(value)) {
      return value.map((value) => value.toString());
    }
  }, [value]);

  const options = useMemo(
    () => {
      if (optionsList) {
        return optionsList
          .map((option, index) => {
            const number = unit.min === 0 ? index : index + 1;

            return {
              value: number.toString(),
              label: option,
            };
          })
          .filter(filterOption);
      }

      return [...Array(unit.total)]
        .map((e, index) => {
          const number = unit.min === 0 ? index : index + 1;

          return {
            value: number.toString(),
            label: formatValue(
              number,
              unit,
              humanizeLabels,
              leadingZero,
              clockFormat
            ),
          };
        })
        .filter(filterOption);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionsList, leadingZero, humanizeLabels, clockFormat]
  );
  const localeJSON = JSON.stringify(locale);
  const renderTag = useCallback(
    (props) => {
      const { value: itemValue } = props;

      if (!value || value[0] !== Number(itemValue)) {
        return <></>;
      }

      const parsedArray = parsePartArray(value, unit);
      const cronValue = partToString(
        parsedArray,
        unit,
        humanizeLabels,
        leadingZero,
        clockFormat
      );
      const testEveryValue = cronValue.match(/^\*\/([0-9]+),?/) || [];

      return (
        <div>
          {testEveryValue[1]
            ? `${locale.everyText || DEFAULT_LOCALE_EN.everyText} ${
                testEveryValue[1]
              }`
            : cronValue}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, localeJSON, humanizeLabels, leadingZero, clockFormat]
  );

  const simpleClick = useCallback(
    (newValueOption) => {
      const newValueOptions = Array.isArray(newValueOption)
        ? sort(newValueOption)
        : [newValueOption];
      let newValue = newValueOptions;

      if (value) {
        newValue = mode === "single" ? [] : [...value];

        newValueOptions.forEach((o) => {
          const newValueOptionNumber = Number(o);

          if (value.some((v) => v === newValueOptionNumber)) {
            newValue = newValue.filter((v) => v !== newValueOptionNumber);
          } else {
            newValue = sort([...newValue, newValueOptionNumber]);
          }
        });
      }

      if (newValue.length === unit.total) {
        setValue([]);
      } else {
        setValue(newValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, value]
  );

  const doubleClick = useCallback(
    (newValueOption) => {
      if (newValueOption !== 0 && newValueOption !== 1) {
        const limit = unit.total + unit.min;
        const newValue = [];

        for (let i = unit.min; i < limit; i++) {
          if (i % newValueOption === 0) {
            newValue.push(i);
          }
        }
        const oldValueEqualNewValue =
          value &&
          newValue &&
          value.length === newValue.length &&
          value.every((v, i) => v === newValue[i]);
        const allValuesSelected = newValue.length === options.length;

        if (allValuesSelected) {
          setValue([]);
        } else if (oldValueEqualNewValue) {
          setValue([]);
        } else {
          setValue(newValue);
        }
      } else {
        setValue([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, options, setValue]
  );

  const clicksRef = useRef([]);
  const onOptionClick = useCallback(
    (newValueOption) => {
      if (!readOnly) {
        const doubleClickTimeout = 300;
        const clicks = clicksRef.current;

        clicks.push({
          time: new Date().getTime(),
          value: Number(newValueOption),
        });

        const id = window.setTimeout(() => {
          if (
            periodicityOnDoubleClick &&
            clicks.length > 1 &&
            clicks[clicks.length - 1].time - clicks[clicks.length - 2].time <
              doubleClickTimeout
          ) {
            if (
              clicks[clicks.length - 1].value ===
              clicks[clicks.length - 2].value
            ) {
              doubleClick(Number(newValueOption));
            } else {
              simpleClick([
                clicks[clicks.length - 2].value,
                clicks[clicks.length - 1].value,
              ]);
            }
          } else {
            simpleClick(Number(newValueOption));
          }

          clicksRef.current = [];
        }, doubleClickTimeout);

        return () => {
          window.clearTimeout(id);
        };
      }
    },
    [clicksRef, simpleClick, doubleClick, readOnly, periodicityOnDoubleClick]
  );

  // Used by the select clear icon
  const onClear = useCallback(() => {
    if (!readOnly) {
      setValue([]);
    }
  }, [setValue, readOnly]);

  const internalClassName = useMemo(
    () =>
      classNames({
        "react-js-cron-select": true,
        "react-js-cron-custom-select": true,
        [`${className}-select`]: !!className,
      }),
    [className]
  );

  const popupClassName = useMemo(
    () =>
      classNames({
        "react-js-cron-select-dropdown": true,
        [`react-js-cron-select-dropdown-${unit.type}`]: true,
        "react-js-cron-custom-select-dropdown": true,
        [`react-js-cron-custom-select-dropdown-${unit.type}`]: true,
        [`react-js-cron-custom-select-dropdown-minutes-large`]:
          unit.type === "minutes" && period !== "hour" && period !== "day",
        [`react-js-cron-custom-select-dropdown-minutes-medium`]:
          unit.type === "minutes" && (period === "day" || period === "hour"),
        "react-js-cron-custom-select-dropdown-hours-twelve-hour-clock":
          unit.type === "hours" && clockFormat === "12-hour-clock",
        "react-js-cron-custom-select-dropdown-grid": !!grid,
        [`${className}-select-dropdown`]: !!className,
        [`${className}-select-dropdown-${unit.type}`]: !!className,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [className, grid, clockFormat, period]
  );
  return (
    <Select
      // Use 'multiple' instead of 'tags‘ mode
      // cf: Issue #2
      mode={
        mode === "single" && !periodicityOnDoubleClick ? undefined : "multiple"
      }
      allowClear={allowClear ?? !readOnly}
      virtual={false}
      open={readOnly ? false : undefined}
      value={stringValue}
      onClear={onClear}
      tagRender={renderTag}
      className={internalClassName}
      popupClassName={popupClassName}
      options={options}
      showSearch={false}
      showArrow={!readOnly}
      // suffixIcon={readOnly ? null : undefined}
      menuItemSelectedIcon={null}
      popupMatchSelectWidth={false}
      onSelect={onOptionClick}
      onDeselect={onOptionClick}
      disabled={disabled}
      dropdownAlign={
        (unit.type === "minutes" || unit.type === "hours") &&
        period !== "day" &&
        period !== "hour"
          ? {
              // Usage: https://github.com/yiminghe/dom-align
              // Set direction to left to prevent dropdown to overlap window
              points: ["tr", "br"],
            }
          : undefined
      }
      data-testid={`custom-select-${unit.type}`}
      {...otherProps}
    />
  );
}

export function WeekDays(props) {
  const {
    value,
    setValue,
    locale,
    className,
    humanizeLabels,
    monthDays,
    disabled,
    readOnly,
    period,
    periodicityOnDoubleClick,
    mode,
    allowClear,
    filterOption,
  } = props;
  const optionsList = locale.weekDays || DEFAULT_LOCALE_EN.weekDays;
  const noMonthDays = period === "week" || !monthDays || monthDays.length === 0;
  const internalClassName = useMemo(
    () =>
      classNames({
        "react-js-cron-field": true,
        "react-js-cron-week-days": true,
        "react-js-cron-week-days-placeholder": !noMonthDays,
        [`${className}-field`]: !!className,
        [`${className}-week-days`]: !!className,
      }),
    [className, noMonthDays]
  );
  const localeJSON = JSON.stringify(locale);
  const placeholder = useMemo(
    () => {
      if (noMonthDays) {
        return locale.emptyWeekDays || DEFAULT_LOCALE_EN.emptyWeekDays;
      }

      return locale.emptyWeekDaysShort || DEFAULT_LOCALE_EN.emptyWeekDaysShort;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noMonthDays, localeJSON]
  );
  const displayWeekDays =
    period === "week" ||
    !readOnly ||
    (value && value.length > 0) ||
    ((!value || value.length === 0) && (!monthDays || monthDays.length === 0));
  const monthDaysIsDisplayed =
    !readOnly ||
    (monthDays && monthDays.length > 0) ||
    ((!monthDays || monthDays.length === 0) && (!value || value.length === 0));

  return displayWeekDays ? (
    <div className={internalClassName}>
      {locale.prefixWeekDays !== "" &&
        (period === "week" || !monthDaysIsDisplayed) && (
          <span>
            {locale.prefixWeekDays || DEFAULT_LOCALE_EN.prefixWeekDays}
          </span>
        )}

      {locale.prefixWeekDaysForMonthAndYearPeriod !== "" &&
        period !== "week" &&
        monthDaysIsDisplayed && (
          <span>
            {locale.prefixWeekDaysForMonthAndYearPeriod ||
              DEFAULT_LOCALE_EN.prefixWeekDaysForMonthAndYearPeriod}
          </span>
        )}
      <CustomSelect
        placeholder={placeholder}
        optionsList={optionsList}
        grid={false}
        value={value}
        unit={{
          ...UNITS[4],
          alt: locale.altWeekDays || DEFAULT_LOCALE_EN.altWeekDays,
        }}
        setValue={setValue}
        locale={locale}
        className={className}
        humanizeLabels={humanizeLabels}
        disabled={disabled}
        readOnly={readOnly}
        period={period}
        periodicityOnDoubleClick={periodicityOnDoubleClick}
        mode={mode}
        allowClear={allowClear}
        filterOption={filterOption}
      />
    </div>
  ) : null;
}

export function Minutes(props) {
  const {
    value,
    setValue,
    locale,
    className,
    disabled,
    readOnly,
    leadingZero,
    clockFormat,
    period,
    periodicityOnDoubleClick,
    mode,
    allowClear,
    filterOption,
  } = props;
  const internalClassName = useMemo(
    () =>
      classNames({
        "react-js-cron-field": true,
        "react-js-cron-minutes": true,
        [`${className}-field`]: !!className,
        [`${className}-minutes`]: !!className,
      }),
    [className]
  );
  return (
    <div className={internalClassName}>
      {period === "hour"
        ? locale.prefixMinutesForHourPeriod !== "" && (
            <span>
              {locale.prefixMinutesForHourPeriod ||
                DEFAULT_LOCALE_EN.prefixMinutesForHourPeriod}
            </span>
          )
        : locale.prefixMinutes !== "" && (
            <span>
              {locale.prefixMinutes || DEFAULT_LOCALE_EN.prefixMinutes}
            </span>
          )}
      <CustomSelect
        placeholder={
          period === "hour"
            ? locale.emptyMinutesForHourPeriod ||
              DEFAULT_LOCALE_EN.emptyMinutesForHourPeriod
            : locale.emptyMinutes || DEFAULT_LOCALE_EN.emptyMinutes
        }
        value={value}
        unit={UNITS[0]}
        setValue={setValue}
        locale={locale}
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        leadingZero={leadingZero}
        clockFormat={clockFormat}
        period={period}
        periodicityOnDoubleClick={periodicityOnDoubleClick}
        mode={mode}
        allowClear={allowClear}
        filterOption={filterOption}
      />
      {period === "hour" && locale.suffixMinutesForHourPeriod !== "" && (
        <span>
          {locale.suffixMinutesForHourPeriod ||
            DEFAULT_LOCALE_EN.suffixMinutesForHourPeriod}
        </span>
      )}
    </div>
  );
}

export function Hours(props) {
  const {
    value,
    setValue,
    locale,
    className,
    disabled,
    readOnly,
    leadingZero,
    clockFormat,
    period,
    periodicityOnDoubleClick,
    mode,
    allowClear,
    filterOption,
  } = props;
  const internalClassName = useMemo(
    () =>
      classNames({
        "react-js-cron-field": true,
        "react-js-cron-hours": true,
        [`${className}-field`]: !!className,
        [`${className}-hours`]: !!className,
      }),
    [className]
  );

  return (
    <div className={internalClassName}>
      {locale.prefixHours !== "" && (
        <span>{locale.prefixHours || DEFAULT_LOCALE_EN.prefixHours}</span>
      )}
      <CustomSelect
        placeholder={locale.emptyHours || DEFAULT_LOCALE_EN.emptyHours}
        value={value}
        unit={UNITS[1]}
        setValue={setValue}
        locale={locale}
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        leadingZero={leadingZero}
        clockFormat={clockFormat}
        period={period}
        periodicityOnDoubleClick={periodicityOnDoubleClick}
        mode={mode}
        allowClear={allowClear}
        filterOption={filterOption}
      />
    </div>
  );
}
